import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Grid,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import { postFallRecord, postGraphData } from '../../../utils/api';
import { useMutation, useQueryClient } from 'react-query';
import TextInput from '../../../components/TextInput';
import { HEALTH_OVERVIEW_MODAL_TYPES } from '../../../constants';
import { useTranslation } from 'react-i18next';

const AddDataModal = ({ open, handleClose, dataType, userId }) => {
  const { t, i18n } = useTranslation(); // translation hook
  const today = new Date().toISOString().split('T')[0];
  const [fromDate, setFromDate] = useState(today);
  const [fromTime, setFromTime] = useState('');
  const [toDate, setToDate] = useState(today);
  const [toTime, setToTime] = useState('');
  const [value, setValue] = useState('');
  const [secondaryValue, setSecondaryValue] = useState('');
  const [notes, setNotes] = useState('');
  const [location, setLocation] = useState('');
  const [valueError, setValueError] = useState(false);
  const [secondaryValueError, setSecondaryValueError] = useState(false);

  const unit = HEALTH_OVERVIEW_MODAL_TYPES[dataType]?.unit || '';
  const type = HEALTH_OVERVIEW_MODAL_TYPES[dataType]?.type || '';
  const type2 = HEALTH_OVERVIEW_MODAL_TYPES[dataType]?.type2 || '';
  const isBloodPressure = dataType === 'Blood Pressure';

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data) => {
      if (dataType === 'Fall detection') {
        return postFallRecord({ userId, healthData: data });
      }
      return postGraphData({ userId, healthData: data });
    },
    onSuccess: () => {
      if (dataType !== 'Fall detection') {
        queryClient.invalidateQueries(['vitalsGraph']);
        queryClient.invalidateQueries(['activityGraph']);
      }
      if (dataType === 'Fall detection') {
        queryClient.invalidateQueries(['fallDetectionGraph']);
      }
      if (dataType === 'Gait Speed') {
        queryClient.invalidateQueries(['gaitSpeedGraph']);
      }
      resetForm();
      handleClose();
    },
  });

  const handleSubmit = () => {
    setValueError(false);
    setSecondaryValueError(false);
    let hasError = false;
    if (!value && dataType !== 'Fall detection' && dataType !== 'Sleep') {
      setValueError(true);
      hasError = true;
    }

    if (isBloodPressure && !secondaryValue) {
      setSecondaryValueError(true);
      hasError = true;
    }

    if (hasError) {
      return;
    }

    if (dataType === 'Fall detection') {
      const fallData = {
        dateTime: moment(`${fromDate} ${fromTime}`).toISOString(),
        type: 'Drop',
        location: location,
        notes: notes,
        source: 'manual',
      };
      mutation.mutate(fallData);
      return;
    }

    const baseData = {
      userId: userId,
      dateFrom: moment(`${fromDate} ${fromTime}`).toDate(),
      dateTo: toTime
        ? moment(`${toDate} ${toTime}`).toDate()
        : moment(`${fromDate} ${fromTime}`).add(1, 'minute').toDate(),
      sourcePlatform: 'manual',
      sourceDeviceId: 'MANUAL_WEB',
      sourceId: 'MANUAL_WEB',
      sourceName: 'MANUAL_WEB',
      recordingMethod: 'manual',
      workoutSummary: null,
      metadata: {
        note: notes,
        location: '',
      },
      platform: 'MANUAL_WEB',
      phoneName: 'MANUAL_WEB',
      phoneId: 'MANUAL_WEB',
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const sleepDuration =
      dataType === 'Sleep' && toTime
        ? moment(`${toDate} ${toTime}`).diff(moment(`${fromDate} ${fromTime}`), 'minutes', true)
        : Number(value);

    const healthData = isBloodPressure
      ? [
          {
            ...baseData,
            uuid: crypto.randomUUID(),
            value: {
              __type: 'NumericHealthValue',
              numericValue: Number(secondaryValue),
            },
            type: type,
            unit: unit,
          },
          {
            ...baseData,
            uuid: crypto.randomUUID(),
            value: {
              __type: 'NumericHealthValue',
              numericValue: Number(value),
            },
            type: type2,
            unit: unit,
          },
        ]
      : [
          {
            ...baseData,
            uuid: crypto.randomUUID(),
            value: {
              __type: 'NumericHealthValue',
              numericValue: dataType === 'Sleep' ? sleepDuration : Number(value),
            },
            type: type,
            unit: unit,
          },
        ];

    mutation.mutate(healthData);
  };

  const resetForm = () => {
    setFromDate(today);
    setFromTime('');
    setToDate(today);
    setToTime('');
    setValue('');
    setSecondaryValue('');
    setNotes('');
    setLocation('');
    setValueError(false);
    setSecondaryValueError(false);
  };

  // Function to format date for display based on current language
  const formatDisplayDate = (dateString) => {
    return moment(dateString).format('L'); // 'L' uses the locale format
  };

  // Function to get input date value (always in English format YYYY-MM-DD)
  const getInputDateValue = (dateString) => {
    return moment(dateString, 'L').isValid() ? moment(dateString, 'L').format('YYYY-MM-DD') : dateString;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          padding: '8px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          {t('add')} {t(dataType)}
        </Typography>
        <IconButton edge="end" color="inherit" onClick={handleClose} aria-label={t('close')}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '16px 24px' }}>
        <Grid container spacing={3} sx={{ marginTop: '2px' }}>
          <Grid item xs={12} sm={6}>
            <TextInput
              label={t('fromTime')}
              type="time"
              value={fromTime}
              onChange={(e) => setFromTime(e.target.value)}
              fullWidth
              margin="normal"
              inputProps={{
                step: 300,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextInput
              label={t('fromDate')}
              type="date"
              value={getInputDateValue(fromDate)}
              onChange={(e) => setFromDate(e.target.value)}
              fullWidth
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                // Display formatted date but store English format
                placeholder: formatDisplayDate(fromDate),
              }}
            />
          </Grid>

          {(dataType === 'Calories' || dataType === 'Sleep') && (
            <>
              <Grid item xs={12} sm={6}>
                <TextInput
                  label={t('toTime')}
                  type="time"
                  value={toTime}
                  onChange={(e) => setToTime(e.target.value)}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    step: 300,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextInput
                  label={t('toDate')}
                  type="date"
                  value={getInputDateValue(toDate)}
                  onChange={(e) => setToDate(e.target.value)}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    placeholder: formatDisplayDate(toDate),
                  }}
                />
              </Grid>
            </>
          )}

          {isBloodPressure ? (
            <>
              <Grid item xs={12} sm={6}>
                <TextInput
                  label={t('systolic')}
                  type="number"
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                    setValueError(false);
                  }}
                  fullWidth
                  margin="normal"
                  error={valueError}
                  helperText={valueError ? t('requiredField') : ''}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextInput
                  label={t('diastolic')}
                  type="number"
                  value={secondaryValue}
                  onChange={(e) => {
                    setSecondaryValue(e.target.value);
                    setSecondaryValueError(false);
                  }}
                  fullWidth
                  margin="normal"
                  error={secondaryValueError}
                  helperText={secondaryValueError ? t('requiredField') : ''}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
                  }}
                />
              </Grid>
            </>
          ) : (
            dataType !== 'Sleep' && (
              <Grid item xs={12}>
                <TextInput
                  label={t(dataType)}
                  type={dataType === 'Fall detection' ? 'text' : 'number'}
                  value={dataType === 'Fall detection' ? 'DROP' : value}
                  onChange={(e) => {
                    setValue(e.target.value);
                    setValueError(false);
                  }}
                  fullWidth
                  margin="normal"
                  error={valueError}
                  helperText={valueError ? t('requiredField') : ''}
                  disabled={dataType === 'Fall detection'}
                />
              </Grid>
            )
          )}

          {dataType === 'Fall detection' && (
            <Grid item xs={12}>
              <TextInput
                label={t('location')}
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                fullWidth
                margin="normal"
                placeholder={t('locationPlaceholder')}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextInput
              label={t('notes')}
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              margin="normal"
              placeholder={t('notesPlaceholder')}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button
          onClick={handleClose}
          sx={{
            borderRadius: '50px',
            textTransform: 'none',
            padding: '8px 16px',
          }}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            borderRadius: '50px',
            textTransform: 'none',
            padding: '8px 24px',
            backgroundColor: '#3b9b8f',
            '&:hover': {
              backgroundColor: '#2d8a7e',
            },
          }}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDataModal;
